'use client';

import { ChevronDown, ChevronUp } from '@ps/web-fusion-icons';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { useContext } from 'react';
import type { Key, SelectProps as ReactAriaSelectProps } from 'react-aria-components';
import {
  Button,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select as ReactAriaSelect,
  SelectStateContext,
  SelectValue,
  Text
} from 'react-aria-components';

import Icon from '../Icon/Icon';
import { useFusionTheme } from '../hooks';

import styles from './Select.module.scss';

interface Option<T extends Key> {
  id: T;
  name: string;
  [key: string]: unknown;
}

export type SelectProps<K extends Key = Key> = Omit<
  ReactAriaSelectProps<Option<K>>,
  | 'children'
  | 'validate'
  | 'validationBehavior'
  | 'onSelectionChange'
  | 'selectedKey'
  | 'defaultSelectedKey'
> & {
  label?: string;
  selectedKey?: K;
  defaultSelectedKey?: K;
  onSelectionChange?: (key: K) => void;
  children?: React.ReactNode;
  options?: Option<K>[];
} & (
    | { options?: never; children: React.ReactNode }
    | { options: Option<K>[]; children?: React.ReactNode }
  ) &
  VariantProps<typeof selectButtonVariants>;

function SelectButton({ size }: { size?: SelectProps['size'] }) {
  const state = useContext(SelectStateContext);
  return (
    <Button
      className={selectButtonVariants({
        size
      })}
    >
      <SelectValue className={selectValueVariants({ size })} />
      <Icon
        className={iconVariants({ size })}
        icon={state?.isOpen ? ChevronUp : ChevronDown}
        aria-hidden
      />
    </Button>
  );
}

const selectButtonVariants = cva(styles.button, {
  variants: {
    size: {
      medium: '',
      small: styles.buttonSmall
    }
  }
});

const selectValueVariants = cva(styles.selectValue, {
  variants: {
    size: {
      medium: '',
      small: styles.selectValueSmall
    }
  }
});

const iconVariants = cva(styles.icon, {
  variants: {
    size: {
      medium: '',
      small: styles.iconSmall
    }
  }
});

const dropdownMenuItemVariants = cva(styles.dropdownMenuItem, {
  variants: {
    size: {
      medium: '',
      small: styles.dropdownMenuItemSmall
    }
  }
});

export function Select<T extends Key = Key>({
  label,
  children,
  options,
  size,
  selectedKey,
  defaultSelectedKey,
  onSelectionChange,
  className,
  ...props
}: SelectProps<T>) {
  const { addThemeClassName } = useFusionTheme();

  return (
    <ReactAriaSelect<Option<T>>
      {...props}
      selectedKey={selectedKey}
      defaultSelectedKey={defaultSelectedKey}
      onSelectionChange={(key: Key | null) => {
        if (onSelectionChange) onSelectionChange(key as T);
      }}
      className={addThemeClassName(className)}
    >
      {label && <Label className={styles.label}>{label}</Label>}
      <SelectButton size={size} />
      <Popover className={styles.popover} offset={0}>
        {children ? (
          <ListBox<Option<T>> selectionMode="single" className={addThemeClassName()}>
            {children}
          </ListBox>
        ) : (
          options && (
            <ListBox<Option<T>>
              selectionMode="single"
              className={addThemeClassName(styles.dropdownMenu)}
            >
              {options.map((option) => (
                <ListBoxItem<Option<T>>
                  key={option.id}
                  id={option.id}
                  className={dropdownMenuItemVariants({
                    size
                  })}
                >
                  <Text slot="label">{option.name}</Text>
                </ListBoxItem>
              ))}
            </ListBox>
          )
        )}
      </Popover>
    </ReactAriaSelect>
  );
}

export default Select;
